/**
 * Реестр разделов веба. Навигация и маршруты строятся только из него — экранов-заглушек
 * «раздел в разработке» нет. Раздел добавляется файлом `src/features/<раздел>/section.ts`
 * (подхватывается автоматически) — см. src/features/README.md.
 */
import type { CarbonIconType } from '@carbon/icons-react';
import type { i18n as I18nInstance } from 'i18next';
import type { ComponentType } from 'react';
import { namespaceOf } from '@/i18n';
import { CLINIC_ROLES, STAFF_ROLES, type AppRole, type ClinicRole, type StaffRole } from './session/roles';

/**
 * Область раздела:
 * - `clinic` — портал клиники и врача (роли клиники, макет с переключателем клиники), путь от «/»;
 * - `backoffice` — бэк-офис dodoc (роли DODOC_*), путь от «/backoffice»;
 * - `public` — без входа (вход, регистрация, приглашения), публичный макет, путь от «/».
 */
export type SectionArea = 'clinic' | 'backoffice' | 'public';

/**
 * Группы боковой навигации (DESIGN.md 1.5, меню ролей — docs/redesign-2026-09.md). Подписи — `shell:navGroups.<группа>`.
 * `work` и `manage` показываются без видимого заголовка (заголовок есть для экранных дикторов).
 */
export const NAV_GROUPS = {
  clinic: ['work', 'bot', 'exchange', 'manage', 'me'],
  backoffice: ['clients', 'quality', 'library', 'finance', 'support', 'system'],
  public: [],
} as const satisfies Record<SectionArea, readonly string[]>;

/** Группы без видимого заголовка: первая группа меню и группа руководителя. */
export const UNTITLED_NAV_GROUPS: ReadonlySet<string> = new Set(['work', 'manage']);

/**
 * Группы вкладок: несколько разделов — один пункт меню и полоса вкладок над экраном (например, «Профиль и офферы»:
 * профиль, офферы, отзывы, сообщество). Пункт меню ведёт в первый доступный раздел группы. Подписи пунктов —
 * `shell:tabGroups.<группа>`, подписи вкладок — `tabTitleKey` раздела или его `titleKey`.
 */
export const TAB_GROUPS = ['career', 'botSettings', 'knowledge', 'hiring', 'reports', 'clinic'] as const;
export type TabGroup = (typeof TAB_GROUPS)[number];

export type ClinicNavGroup = (typeof NAV_GROUPS.clinic)[number];
export type BackofficeNavGroup = (typeof NAV_GROUPS.backoffice)[number];
export type NavGroup = ClinicNavGroup | BackofficeNavGroup;

export interface SectionDefinition {
  /** Уникальный идентификатор: `<модуль>.<раздел>` (например, `scribe.encounters`). Идёт в контекст обращения в поддержку. */
  id: string;
  area: SectionArea;
  /**
   * Путь внутри области без ведущего «/»: `audit`, `patients/*` (вложенные маршруты раздела).
   * Абсолютный путь (`/_kit`) — полноэкранный раздел со своим макетом (layout: 'fullscreen').
   */
  path: string;
  /** Кому раздел виден и доступен. Для clinic — роли в выбранной клинике, для backoffice — роли DODOC_*. */
  roles: ReadonlyArray<AppRole>;
  icon: CarbonIconType;
  /** Ключ i18n названия раздела: `namespace:key`. Namespace загружается вместе с разделом. */
  titleKey: string;
  /** Дополнительные namespaces экрана (кроме namespace из titleKey). */
  namespaces?: string[];
  /** Группа боковой навигации (для public не нужна). */
  navGroup?: NavGroup;
  /** Порядок внутри группы (меньше — выше). */
  order: number;
  /** Показывать в навигации (по умолчанию — да; false — только маршрут, например страница приглашения). */
  nav?: boolean;
  /**
   * Для каких ролей пункт виден в меню (по умолчанию — все `roles`). Доступ к экрану не меняет: например,
   * расписание открывается врачу по ссылке, но в его меню вместо него «Мой день».
   */
  navRoles?: ReadonlyArray<AppRole>;
  /** Группа вкладок: разделы группы — один пункт меню и вкладки над экраном. */
  tabGroup?: TabGroup;
  /** Короткая подпись вкладки (`namespace:key`); по умолчанию — `titleKey`. */
  tabTitleKey?: string;
  /** Подпись пункта меню, если она короче или другая, чем заголовок экрана (`namespace:key`). */
  navTitleKey?: string;
  /**
   * Счётчик у пункта меню (например, диалоги, ждущие человека). Ленивый компонент раздела рисует число
   * через `NavCount` из оболочки; нет данных или ноль — ничего не рисует.
   */
  navCounter?: () => Promise<{ default: ComponentType }>;
  layout?: 'shell' | 'fullscreen';
  /** Полноэкранный раздел доступен без входа в режиме разработки (витрина /_kit). */
  devPublic?: boolean;
  /** Ленивая загрузка экрана (отдельный чанк сборки, НФТ-06). */
  load: () => Promise<{ default: ComponentType }>;
  /**
   * Фоновый компонент раздела в оболочке: работает на любом экране для ролей раздела (например, звук
   * и уведомление о новой эскалации). Подписывается на события через общую подписку оболочки (useRealtimeEvent),
   * видимой разметки не рисует. Загружается отдельным чанком после входа.
   */
  shellListener?: () => Promise<{ default: ComponentType }>;
}

/** Помощник для типизации: `export const section = defineSection({ … })`. */
export function defineSection(definition: SectionDefinition): SectionDefinition {
  return definition;
}

const TITLE_KEY_RE = /^[a-z][\w-]*:[a-zA-Z][\w.]*$/;
const RELATIVE_PATH_RE = /^[a-z0-9][a-z0-9-]*(\/[a-z0-9:*][a-z0-9-*]*)*$/;
const ABSOLUTE_PATH_RE = /^\/[a-z0-9_][a-z0-9_-]*(\/[a-z0-9:*][a-z0-9-*]*)*$/;

/** Проверка реестра: одинаковые id и пути, неверные роли, ключи и пути — ошибка сборки на этапе тестов. */
export function validateSections(list: ReadonlyArray<SectionDefinition>): string[] {
  const problems: string[] = [];
  const ids = new Set<string>();
  const paths = new Set<string>();
  for (const section of list) {
    if (ids.has(section.id)) problems.push(`duplicate section id ${section.id}`);
    ids.add(section.id);
    const fullscreen = section.layout === 'fullscreen';
    const pathOk = fullscreen ? ABSOLUTE_PATH_RE.test(section.path) : RELATIVE_PATH_RE.test(section.path);
    if (!pathOk) problems.push(`${section.id}: invalid path "${section.path}"`);
    const pathKey = fullscreen ? section.path : `${section.area}:${section.path}`;
    if (paths.has(pathKey)) problems.push(`${section.id}: duplicate path "${section.path}"`);
    paths.add(pathKey);
    if (!TITLE_KEY_RE.test(section.titleKey)) problems.push(`${section.id}: titleKey must be "namespace:key"`);
    for (const key of [section.tabTitleKey, section.navTitleKey]) {
      if (key !== undefined && !TITLE_KEY_RE.test(key)) problems.push(`${section.id}: title keys must be "namespace:key"`);
    }
    if (section.tabGroup && section.area !== 'clinic') problems.push(`${section.id}: tabGroup is only for clinic sections`);
    for (const role of section.navRoles ?? []) if (!section.roles.includes(role)) problems.push(`${section.id}: navRole ${role} is not in roles`);
    if (section.area !== 'public' && section.roles.length === 0) problems.push(`${section.id}: roles are required`);
    const allowed: readonly string[] = section.area === 'backoffice' ? STAFF_ROLES : section.area === 'clinic' ? CLINIC_ROLES : [...CLINIC_ROLES, ...STAFF_ROLES];
    for (const role of section.roles) if (!allowed.includes(role)) problems.push(`${section.id}: role ${role} is not allowed in area ${section.area}`);
    if (section.area !== 'public' && section.nav !== false) {
      const groups: readonly string[] = NAV_GROUPS[section.area];
      if (!section.navGroup || !groups.includes(section.navGroup)) problems.push(`${section.id}: navGroup must be one of ${groups.join(', ')}`);
    }
  }
  return problems;
}

type SectionModule = { section?: SectionDefinition; sections?: SectionDefinition[] };

export function collectSections(modules: Record<string, SectionModule>): SectionDefinition[] {
  const list = Object.values(modules).flatMap((module) => [...(module.section ? [module.section] : []), ...(module.sections ?? [])]);
  return list.sort((a, b) => a.order - b.order || a.id.localeCompare(b.id));
}

/** Разделы портала — `features/<модуль>/section.ts`; разделы бэк-офиса — `features/backoffice/<модуль>/section.ts`. */
const discovered = import.meta.glob<SectionModule>(['../features/*/section.ts', '../features/backoffice/*/section.ts'], { eager: true });

/** Все зарегистрированные разделы. */
export const sections: SectionDefinition[] = collectSections(discovered);

const problems = validateSections(sections);
if (problems.length > 0) throw new Error(`Section registry is invalid:\n${problems.join('\n')}`);

/** Доступен ли раздел пользователю с ролями (клиники или глобальными). */
export function canAccess(section: SectionDefinition, roles: ReadonlyArray<ClinicRole | StaffRole>): boolean {
  if (section.area === 'public') return true;
  return section.roles.some((role) => roles.includes(role));
}

/** Namespaces i18n раздела: из titleKey и дополнительные. */
export function sectionNamespaces(section: SectionDefinition): string[] {
  return [...new Set([namespaceOf(section.titleKey), ...(section.namespaces ?? [])])];
}

/** Полный путь раздела для ссылок. */
export function sectionHref(section: SectionDefinition): string {
  if (section.layout === 'fullscreen' || section.path.startsWith('/')) return section.path;
  const clean = section.path.replace(/\/\*$/, '');
  return section.area === 'backoffice' ? `/backoffice/${clean}` : `/${clean}`;
}

export interface NavGroupView {
  group: NavGroup;
  items: SectionDefinition[];
}

/** Виден ли пункт раздела в меню ролей (доступ + `navRoles`). */
export function showsInNav(section: SectionDefinition, roles: ReadonlyArray<ClinicRole | StaffRole>): boolean {
  if (section.nav === false || !canAccess(section, roles)) return false;
  return !section.navRoles || section.navRoles.some((role) => roles.includes(role));
}

/**
 * Навигация области для ролей: группы в порядке `NAV_GROUPS`, пустые не показываются. Разделы одной группы
 * вкладок сворачиваются в один пункт — первый доступный раздел группы.
 */
export function buildNavigation(list: ReadonlyArray<SectionDefinition>, area: 'clinic' | 'backoffice', roles: ReadonlyArray<ClinicRole | StaffRole>): NavGroupView[] {
  const seenTabGroups = new Set<string>();
  const visible = list.filter((section) => {
    if (section.area !== area || !showsInNav(section, roles)) return false;
    if (!section.tabGroup) return true;
    if (seenTabGroups.has(section.tabGroup)) return false;
    seenTabGroups.add(section.tabGroup);
    return true;
  });
  return (NAV_GROUPS[area] as readonly NavGroup[])
    .map((group) => ({ group, items: visible.filter((section) => section.navGroup === group) }))
    .filter((entry) => entry.items.length > 0);
}

/** Подпись пункта меню: группа вкладок → её название; иначе `navTitleKey` или заголовок экрана. */
export function navTitleKey(section: SectionDefinition): string {
  if (section.tabGroup) return `shell:tabGroups.${section.tabGroup}`;
  return section.navTitleKey ?? section.titleKey;
}

/** Вкладки экрана: доступные ролям разделы той же группы вкладок, по порядку (пусто — вкладок нет). */
export function tabSections(section: SectionDefinition, roles: ReadonlyArray<ClinicRole | StaffRole>, list: ReadonlyArray<SectionDefinition> = sections): SectionDefinition[] {
  if (!section.tabGroup) return [];
  const members = list.filter((item) => item.tabGroup === section.tabGroup && canAccess(item, roles));
  return members.length > 1 ? members : [];
}

/** Раздел, которому принадлежит адрес (самый длинный совпавший путь). */
export function sectionForPath(pathname: string, list: ReadonlyArray<SectionDefinition> = sections): SectionDefinition | null {
  let best: SectionDefinition | null = null;
  for (const section of list) {
    const href = sectionHref(section);
    if ((pathname === href || pathname.startsWith(`${href}/`)) && (!best || href.length > sectionHref(best).length)) best = section;
  }
  return best;
}

/**
 * Первый экран быстрее (НФТ-06): код и переводы раздела из адреса начинают грузиться сразу при запуске,
 * параллельно с проверкой сессии, а не после неё. Права проверяются как обычно — загрузка кода их не обходит.
 */
export function prefetchSectionForPath(pathname: string, i18n: Pick<I18nInstance, 'loadNamespaces'>, list: ReadonlyArray<SectionDefinition> = sections): SectionDefinition | null {
  const section = sectionForPath(pathname, list);
  if (!section) return null;
  section.load().catch(() => {
    /* сеть недоступна — SectionPage покажет ошибку и повтор при открытии */
  });
  void i18n.loadNamespaces(sectionNamespaces(section));
  return section;
}
